

import { UserOutlined, LogoutOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { Dropdown } from "antd";

import logo from "../../assets/images/MahaTransco_logo.png";
import "./PageHeader.css";
import useAuth from "../../hooks/auth/useAuth";

function PageHeader({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  const isGIS = location.pathname === "/";

  // Auto-hide header on scroll down
  useEffect(() => {
    if (isGIS) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, isGIS]);

  const pageTitle = useMemo(() => {
    const path = location.pathname;
    if (path === "/") return "GIS Workspace";
    if (path === "/dashboard") return "Dashboard";
    if (path.startsWith("/analytics/drilldown")) return "Analytics Drilldown";
    if (path === "/schemes") return "Schemes";
    if (/^\/schemes\/[^/]+$/.test(path)) return "Scheme Details";
    if (/^\/schemes\/[^/]+\/edit$/.test(path)) return "Edit Scheme";
    if (path === "/users") return "Users Management";
    return "MahaTransco";
  }, [location.pathname]);

  const fullName = user?.full_name || user?.name || "User";
  const firstName = fullName.split(" ")[0];
  const hideOrganizationFields = user?.role === "CHIEF_ENGINEER";

  const userDropdownContent = (
    <div className="user-dropdown-card">
      <div className="user-card-header">
        <div className="user-avatar">
          <UserOutlined style={{ fontSize: 32, color: "#fff" }} />
        </div>

        <div className="user-details">
          <div className="user-name">{fullName}</div>
          <div className="user-email">{user?.email}</div>
        </div>
      </div>

      <div className="user-card-body">
        {/* User ID */}
        {user?.id && (
          <div className="user-info-row">
            <span className="label">ID</span>
            <span className="value">#{user.id}</span>
          </div>
        )}

        {/* Role */}
        {user?.role && (
          <div className="user-info-row">
            <span className="label">Role</span>
            <span className="value">
              {user.role.replace(/_/g, " ")}
            </span>
          </div>
        )}

        {/* Zone */}
      {!hideOrganizationFields && user?.zone_name && (
          <div className="user-info-row">
            <span className="label">Zone</span>
            <span className="value">{user.zone_name}</span>
          </div>
        )}

        {/* Circle */}
        {!hideOrganizationFields && user?.circle_name && (
            <div className="user-info-row">
              <span className="label">Circle</span>
              <span className="value">{user.circle_name}</span>
            </div>
          )}

        {/* Division */}
        {!hideOrganizationFields && user?.div_name && (
          <div className="user-info-row">
            <span className="label">Division</span>
            <span className="value">{user.div_name}</span>
          </div>
        )}

        {/* Subdivision */}
       {!hideOrganizationFields && user?.subdiv_name && (
          <div className="user-info-row">
            <span className="label">Sub Division</span>
            <span className="value">{user.subdiv_name}</span>
          </div>
        )}
      </div>

      <div className="user-card-footer">
        <button
          className="logout-btn"
          onClick={() => {
            setOpen(false);
            logout();
          }}
        >
          <LogoutOutlined style={{ marginRight: 8 }} />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <header
      className={`page-header ${
        isGIS ? "gis-header" : "default-header"
      } ${isHidden ? "header-hidden" : ""}`}
    >
      <div className="header-left">
        <img
          src={logo}
          alt="MahaTransco"
          className={`header-logo ${isGIS ? "gis-logo" : ""}`}
        />

        {!isGIS && (
          <>
            <div className="header-divider" />
            <h2 className="header-section-title" title={pageTitle}>
              {pageTitle}
            </h2>
          </>
        )}
      </div>

      {isGIS && <div className="header-center">{children}</div>}

      <div className="header-right">
        <Dropdown
          open={open}
          onOpenChange={setOpen}
          placement="bottomRight"
          trigger={["click"]}
          dropdownRender={() => userDropdownContent}
          overlayStyle={{ zIndex: 1100 }}
        >
          <div className="user-trigger">
            <UserOutlined
              style={{ fontSize: 26, color: "#da241c" }}
            />
            <span className="user-name-trigger">{firstName}</span>
          </div>
        </Dropdown>
      </div>
    </header>
  );
}

export default PageHeader;